import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  Menu,
  MenuItem,
  Avatar,
  TextField,
  InputAdornment,
} from '@mui/material';
import { 
  Add, 
  Refresh, 
  Edit, 
  Delete, 
  MoreVert, 
  ArrowBack,
  MiscellaneousServices,
  Search,
  Visibility
} from '@mui/icons-material';
import { 
  getServiceSubCategories, 
  deleteServiceSubCategory 
} from '../../../services/Api';
import DeleteConfirm from '../../../modals/DeleteConfirm';

const DisplaySubServices = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  
  const [subServices, setSubServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuSelectedService, setMenuSelectedService] = useState(null);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchSubServices = async (page = 1, limit = 10, search = '') => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getServiceSubCategories(page, limit, categoryId, search);
      
      if (response.success) {
        setSubServices(response.data || []);
        setTotalRecords(response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || response.totalRecords || 0 || 0);
      }
    } catch (err) {
      console.error('Error fetching sub services:', err);
      setError(err.response?.data?.message || 'Failed to fetch sub services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubServices(1, 10, debouncedSearch);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, debouncedSearch]);

  const handleMenuOpen = (event, service) => {
    setAnchorEl(event.currentTarget);
    setMenuSelectedService(service);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSelectedService(null);
  };

  const handleDeleteClick = () => {
    setServiceToDelete(menuSelectedService.id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (!serviceToDelete) return;
    try {
      setLoading(true);
      setError(null);
      const response = await deleteServiceSubCategory(serviceToDelete);
      if (response.success) {
        setDeleteDialogOpen(false);
        setServiceToDelete(null);
        fetchSubServices();
      } else {
        console.error('Failed to delete sub service');
        setError(response.message || 'Failed to delete sub service');
        setDeleteDialogOpen(false);
      }
    } catch (err) {
      console.error('An error occurred while deleting the sub service:', err);
      setError(err.response?.data?.message || err.message || 'An error occurred while deleting the sub service');
      setDeleteDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleViewServiceOptions = (subServiceId) => {
    // Navigate to service options page for this sub-service
    navigate(`/services/service-options/${subServiceId}`);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchSubServices(newPage + 1, rowsPerPage, debouncedSearch);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchSubServices(1, newRowsPerPage, debouncedSearch);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setPage(0);
    fetchSubServices(1, rowsPerPage, '');
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/services')} 
            sx={{ color: 'primary.main' }}
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 300, color: 'text.primary' }}>
              Sub Services
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and view service sub-categories
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search by name..."
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ 
              width: 180, 
              '& .MuiOutlinedInput-root': { 
                borderRadius: '20px', 
                bgcolor: 'rgba(0,0,0,0.02)' 
              } 
            }}
            InputProps={{ 
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ) 
            }}
          />
          <IconButton onClick={handleRefresh} sx={{ color: 'primary.main' }}>
            <Refresh />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate(`/services/${categoryId}/sub-services/create`)}
            sx={{ px: 4, backgroundColor: 'primary.main' }}
          >
            Add Sub Service
          </Button>
        </Box>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      ) : null}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="sub services table">
            <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && subServices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : subServices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                    <MiscellaneousServices sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
                    <Typography color="text.secondary">
                      {searchTerm ? 'No sub services found matching your search' : 'No sub services found'}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => navigate(`/services/${categoryId}/sub-services/create`)}
                      sx={{ mt: 2 }}
                    >
                      Create your first sub service
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                subServices.map((service) => (
                  <TableRow key={service.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <Avatar
                        variant="rounded"
                        src={service.image}
                        sx={{ width: 60, height: 40, border: '1px solid', borderColor: 'text.border' }}
                      >
                        <MiscellaneousServices />
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {service.name || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {service?.category?.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={service.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={service.isActive ? 'success' : 'error'}
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      {service.createdAt 
                        ? new Date(service.createdAt).toLocaleDateString()
                        : 'N/A'
                      }
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, service)}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {subServices.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 150,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
            },
          },
        }}
      >
        <MenuItem onClick={() => {
          navigate(`/services/${categoryId}/sub-services/edit/${menuSelectedService?.id}`);
          handleMenuClose();
        }}>
          <Edit sx={{ mr: 1, fontSize: 18 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => {
          handleViewServiceOptions(menuSelectedService?.id);
          handleMenuClose();
        }}>
          <Visibility sx={{ mr: 1, fontSize: 18 }} />
          Service Options
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1, fontSize: 18 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirm
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setServiceToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Sub Service"
        message="Are you sure you want to delete this sub service? This action cannot be undone."
      />
    </Box>
  );
};

export default DisplaySubServices;
